<template>
  <div
    :key="forceReRenderKey"
    :style="panelBarPositionStyle"
    :pos="optionPosition"
    :p-width="panel.width"
    :p-height="panel.height"
    :class="['panel-top-bar', ['top', 'bottom'].includes(optionPosition) ? 'sideways' : '']"
  >
    <div
      :class="['icon-container', panelEditMode ? 'active bg-highlight' : '']"
      @click="$emit('switchToPanelEdit')">
      <icon size="0.8" type="pencil-alt"/>
      <div class="hover-label">{{ $t('edit') }}</div>
    </div>

    <div
      v-if="!layoutPosition"
      :class="['icon-container', panel.autoFit ? 'active bg-highlight' : '']"
      @click="$emit('setPanelAutoFit', !panel.autoFit)">
      <icon type="arrows-up-down"/>
      <div class="hover-label">{{ $t('autoFit') }}</div>
    </div>

    <div
        v-if="!layoutPosition"
      :class="['icon-container', panel.collapsible ? 'active bg-highlight' : '', panel.pin ? 'usable' : 'unusable']"
      @click="panel.pin ? togglePanelCollapsible(panel) : () => {}">
      <icon :type="getArrowIcon(panel)" class="collapsible-icon"/>
      <div class="hover-label">{{ panel.pin ? $t('makeCollapsible') : $t('haveToPinFirst') }}</div>
    </div>

    <div
      v-if="panel.pin && panel.collapsible"
      :class="[
        'icon-container',
        !panel.collapsed ? 'bg-disabled' : '',
        panel.initiallyCollapsed ? 'active bg-highlight' : '',
      ]"
      @click="setPanelInitiallyCollapsed">
      <icon type="minus"/>
      <div class="hover-label">Initially Collapsed</div>
    </div>

    <div
      v-if="!layoutPosition"
      :class="['icon-container', 'pin-container-toggle', !!panel.pin ? 'active bg-highlight' : '']">
      <icon class="pin-icon" type="thumbtack"/>
      <div class="pin-container-outer-popup">
        <div class="hover-box">
          <div class="pin-container-row">
            <div :class="['icon-container', panel.pin === pinPositions.TOP_LEFT ? 'active bg-highlight' : '']"
                 @click="$emit('togglePinButton', pinPositions.TOP_LEFT, 'collapse-left');">
              <icon type="arrow-up-left"/>
            </div>
            <div :class="['icon-container', panel.pin === pinPositions.TOP ? 'active bg-highlight' : '']"
                 @click="$emit('togglePinButton', pinPositions.TOP, 'collapse-top');">
              <icon type="arrow-up"/>
            </div>
            <div :class="['icon-container', panel.pin === pinPositions.TOP_RIGHT ? 'active bg-highlight' : '']"
                 @click="$emit('togglePinButton', pinPositions.TOP_RIGHT, 'collapse-right');">
              <icon type="arrow-up-right"/>
            </div>
          </div>

          <div class="pin-container-row">
            <div :class="['icon-container', panel.pin === pinPositions.LEFT ? 'active bg-highlight' : '']"
                 @click="$emit('togglePinButton', pinPositions.LEFT, 'collapse-left');">
              <icon type="arrow-left"/>
            </div>
            <div :class="['icon-container', panel.pin === pinPositions.RIGHT ? 'active bg-highlight' : '']"
                 @click="$emit('togglePinButton', pinPositions.RIGHT, 'collapse-right');">
              <icon type="arrow-right"/>
            </div>
          </div>

          <div class="pin-container-row">
            <div :class="['icon-container', panel.pin === pinPositions.BOTTOM_LEFT ? 'active bg-highlight' : '']"
                 @click="$emit('togglePinButton', pinPositions.BOTTOM_LEFT, 'collapse-left');">
              <icon type="arrow-down-left"/>
            </div>
            <div :class="['icon-container', panel.pin === pinPositions.BOTTOM ? 'active bg-highlight' : '']"
                 @click="$emit('togglePinButton', pinPositions.BOTTOM, 'collapse-bottom');">
              <icon type="arrow-down"/>
            </div>
            <div :class="['icon-container', panel.pin === pinPositions.BOTTOM_RIGHT ? 'active bg-highlight' : '']"
                 @click="$emit('togglePinButton', pinPositions.BOTTOM_RIGHT, 'collapse-right');">
              <icon type="arrow-down-right"/>
            </div>
          </div>

          <div class="pin-container-row" style="justify-content: center;">
            <div class="settings-button d-inline-block mt-1" @click="$emit('removePanelPin')">
              {{ $t('clearPin') }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="!layoutPosition" :class="['icon-container', panel.hideBottomBar ? 'active bg-highlight' : '']"
         @click="$emit('setPanelBottomBar')">
      <icon class="eye-icon" :type="panel.hideBottomBar ? 'eye-slash' : 'eye'"/>
      <div class="hover-label">{{ $t('hideBottomBar') }}</div>
    </div>

    <div
      v-if="layoutPositions && Object.keys(layoutPositions).length > 0"
      :class="['icon-container', 'layout-options-toggle', optionPosition, layoutPosition ? 'active' : 'inactive']"
      @click="$emit('setPanelBottomBar')">
      <icon class="eye-icon" type="table-layout"/>
      <!--<div class="hover-label">{{ $t('layoutOptions') }}</div>-->
      <div class="layout-options-popup">
        <div class="hover-box panel-pos">
          <div
            v-for="pos in layoutPositions"
            :key="pos"
            :class="['list-item p-1 d-flex align-items-center justify-content-between', layoutPosition === pos ? 'active' : '']">
            <span @click.stop="$emit('setPanelPosition', pos)" class="flex-grow-1">
              {{ $t('vform.' + pos) }}
            </span>
            <span
              v-if="panel.isMaster && hasEligibleStepsForPosition(pos)"
              :title="$t('vform.applyLayoutPositionToAllSteps')"
              class="apply-all-btn ml-1"
              @click.stop="$emit('applyLayoutPositionToAllSteps', pos)">
              <icon type="globe" size="0.7"/>
            </span>
          </div>
          <div
            v-if="layoutPosition"
            class="list-item p-1"
            @click.stop="$emit('setPanelPosition', null)">
            Free Position
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {pinPositions} from "@/enum";
import Icon from "@/components/Icon.vue";

export default {
  name: "PanelOptions.vue",
  components: {Icon},
  props: {
    panelEditMode: {type: Boolean, default: false},
    optionPosition: {type: String, default: ''},
    panel: {type: Object, required: true},
    layoutPosition: {type: Object, default: null},
    layoutPositions: {type: Object, default: () => {}},
    steps: {type: Array, default: () => []},
  },
  data() {
    return {
      pinPositions: pinPositions,
      forceReRenderKey: 0,
    }
  },
  computed: {
    panelBarPositionStyle() {
      if (this.optionPosition === 'middle') {
        return {
          left: '6px',
          top: '50%',
          height: 'auto',
          transform: 'translateY(-50%)',
        };
      }
      const allowedPositions = ['left', 'right', 'top', 'bottom'];
      const pos = allowedPositions.includes(this.optionPosition) ? this.optionPosition : 'left';
      let defStyle = {
        [pos]: '-32px',
      };
      if(['left', 'right'].includes(pos)) {
        defStyle = {
          ...defStyle,
          top: '50%',
          height: 'auto',
          transform: 'translateY(-50%)',
        }
      }
      return defStyle;
    }
  },
  methods: {
    setPanelInitiallyCollapsed() {
      this.$set(this.panel, 'initiallyCollapsed', !this.panel.initiallyCollapsed);
    },
    togglePanelCollapsible() {
      this.$set(this.panel, 'collapsible', !this.panel.collapsible);
    },
    getArrowIcon() {
      const {pin} = this.panel;
      if (!pin) {
        return 'angles-left';
      }
      if (pin.toLowerCase().includes('top')) {
        return 'angles-up';
      } else if (pin.toLowerCase().includes('bottom')) {
        return 'angles-down';
      } else if (pin.toLowerCase().includes('left')) {
        return 'angles-left';
      } else if (pin.toLowerCase().includes('right')) {
        return 'angles-right';
      }
    },
    /**
     * Returns true if there is at least one OTHER step (not the current one) where:
     *   - this global panel is visible
     *   - the step has a layout with this position enabled and unoccupied
     */
    hasEligibleStepsForPosition(positionName) {
      if (!this.panel.isMaster || !this.panel.globalVisibleOnSteps) return false;
      return this.steps.some(step => {
        if (!this.panel.globalVisibleOnSteps.includes(step.uuid)) return false;
        if (!step.layout || !step.layout.positions) return false;
        const constant = Object.keys(step.layout.positions).find(k =>
          step.layout.positions[k] && step.layout.positions[k].name === positionName
        );
        if (!constant) return false;
        return !!step.layout.positions[constant].enabled;
      });
    },
  }
}
</script>

<style lang="scss" scoped>
.panel-top-bar {
  cursor: pointer;
  padding-top: 15px;
  padding-bottom: 15px;
  transition: all 300ms ease;
  position: absolute;
  width: 30px;
  z-index: 156;
  height: 100%;
  font-family: "Inter", sans-serif !important;

  .icon-container {
    z-index: 5;
  }

  &.sideways {
    display: flex;
    width: auto;
    left: 50%;
    transform: translateX(-50%);
    padding: 0;
    flex-grow: 0;
    height: auto;
  }
}

.hover-label {
  position: absolute;
  left: 100%;
  top: 0;
  background-color: rgba(0, 0, 0, 1);
  padding: 3px;
  border-radius: 3px;
  display: inline-block;
  min-width: 200px;
  opacity: 0;
  pointer-events: none;
  font-size: 0.9rem;
  z-index: 5;
}
.sideways {
  .hover-label {
    top: 25px;
    left: 0;
  }
}

.icon-container {
  opacity: 0.6;
  z-index: 4;
  border-radius: 50%;
  width: 25px;
  height: 25px;
  margin: auto;
  margin-bottom: 1.5px;
  margin-top: 1.5px;
  background-color: var(--ccdefault-normal-button-hover-background);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: #fff;
  position: relative;

  &:hover {
    opacity: 1;
    background-color: var(--ccdefault-normal-button-hover-background);

    .hover-label {
      animation: forwards 0.5s fadeIn;
      animation-delay: 0.5s;
    }
  }

  &.active {
    background-color: $highlight;
  }

  &.bg-highlight {
    background-color: #c55303;
  }

  &.usable {
    cursor: pointer;
  }

  &.unusable {
    cursor: not-allowed;

    &:hover {
      .hover-label {
        animation: none;
        opacity: 1;
      }
    }
  }
}

.icon {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 0.9rem !important;
  display: inline-block;

  &.collapsible-icon, &.pin-icon {
    margin-top: 1px;
  }

  &.eye-icon {
    margin-top: 1px;
    margin-left: 1px;
  }
}

.panel-bottom-icons {
  position: absolute;
  bottom: 15px;
  left: 50%;
  transform: translateX(-50%);
  padding-bottom: 0;
  z-index: 100;
}

.pin-container-toggle {
  .pin-container-outer-popup {
    opacity: 0;
    display: none;
  }

  .settings-button {
    width: 100%;
    text-align: center;
    font-size: 0.75rem;
  }

  &:hover, &:focus {
    z-index: 200;
    .pin-container-outer-popup {
      display: block;
      opacity: 1;
      position: absolute;
      z-index: 15;
      right: -131px;
      padding: 15px 15px 15px 15px;
      top: -63px;
    }
  }
}

.layout-options-toggle {
  .layout-options-popup {
    opacity: 0;
    visibility: hidden;
    pointer-events: none;
    position: absolute;
    z-index: 15;
    left: calc(100% - 2px);
    top: -30%;
    transform: translateY(-50%);
    padding-left: 6px;
    transition: opacity 150ms ease;
  }
  &.bottom {
    .layout-options-popup {
      top: 0;
      transform: none;
    }
  }
  &.top {
    .layout-options-popup {
      top: 0;
      transform: translateY(-70%);
    }
  }

  &:hover,
  &:focus-within {
    .layout-options-popup {
      opacity: 1;
      visibility: visible;
      pointer-events: auto;
    }
  }
}

.hover-box {
  background-color: gray;
  border-radius: 5px;
  color: #fff;

  .list-item {
    white-space:nowrap;
    &:hover, &.active {
      background-color: rgba(0, 0, 0, 0.25);
    }
  }
}

.apply-all-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.15);
  cursor: pointer;
  flex-shrink: 0;
  transition: background-color 150ms ease;
  position: relative;

  .icon {
    opacity: 0.5;
  }
  &:hover {
    background-color: rgba(255, 255, 255, 0.35);
    .icon {
      opacity: 1;
    }
  }

}

.pin-container-row {
  display: flex;
  justify-content: space-between;
  opacity: 1;

  .icon-container {
    margin: 3px;
    padding: 0;
    width: 30px;
    height: 30px;
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}
</style>